import type { Cover as Book3DCover, PageBlock as Book3DBlock } from "@pdfking/book3d"
import type { Distance, Margins, Rectangle } from "@pdfking/math"
import type { Radian } from "@pdfking/math"
import { Max, ToNumber, Total } from "@pdfking/math"
import { ZERO_DISTANCE, ZERO_POINT } from "@pdfking/math/constants"
import { CM, MM } from "@pdfking/quantity"
import type { PageNumber } from "@pdfking/renderer-api"
import type { ContentRenderer } from "@pdfking/templates"
import { GrowContentKind } from "@pdfking/templates"
import { CoverGrowList } from "@pdfking/templates/cover"
import { RecordMerge } from "@pdfking/tfunc"

import type { ComponentSchema, GrowLeaf, GrowSize, ImpositionSchema } from "#internal"
import type { NumberedPage, Numbering } from "#internal"
import type { Meta3D } from "#public"

import { BuildBookBlock } from "../../common/BuildBookBlock"
import { GrowFolds } from "../../common/grow/GrowFolds"
import { GrowOutline } from "../../common/grow/GrowOutline"
import { GrowPieces } from "../../common/grow/GrowPieces"
import { GrowSideSize } from "../../common/grow/GrowSideSize"
import { ResolveGrow } from "../../common/grow/ResolveGrow"
import { StandBoards } from "../../common/grow/StandBoards"
import { ComponentSecurity } from "../presets/ComponentSecurity"
import { ApplyOrientation } from "../utils/ApplyOrientation"

type CounterPage = NumberedPage

// The component a page is of: its path starts at the component, counted
// from 1.
const component_of = (input: ImpositionSchema, page: CounterPage) =>
  input.component_array[page.sequence[0]! - 1]!

// The copies a page's component prints of it in each product
const copies_of = (input: ImpositionSchema, page: CounterPage): number =>
  component_of(input, page).copies

// The pages in blocks by the sides they print, what their grow draws them as,
// their security margins and how many copies of them each product holds; a
// component that prints nothing a blank block where it lies, a sheet a run.
// The cover drawn round the book is none of them.
const build_blocks = (
  input: ImpositionSchema,
  pages: CounterPage[],
  size: Rectangle<Distance>,
  total_depth: Distance,
  cover: ComponentSchema | undefined
): Book3DBlock[] => {
  // the pages each block holds
  const groups = new Map<string, number>()
  const securities = new Map<string, Margins<Distance>>()
  const add = (key: string, security: Margins<Distance>, count: number) => {
    groups.set(key, (groups.get(key) ?? 0) + count)
    securities.set(key, security)
  }
  input.component_array.forEach((component, ci) => {
    if (component === cover) return
    const security = ComponentSecurity(component)
    const { top, right, bottom, left } = security
    const margins = `${top},${right},${bottom},${left}`
    if (!component.job_array.length) {
      const key = `false,false,${component.grow_kind.renderer},${component.copies},${margins}`
      add(key, security, component.repetitions || 1)
      return
    }
    for (const page of pages.filter(page => page.sequence[0] === ci + 1)) {
      const key = `${page.meta.print_front},${page.meta.print_rear},${page.meta.renderer},${copies_of(input, page)},${margins}`
      add(key, security, 1)
    }
  })

  // A block's pages printed `copies` times over make that many runs of its
  // sheets, the spine shared by every sheet of every run
  const grouped = [...groups.entries()].map(([key, count]) => {
    const [pf, pr, renderer, copies] = key.split(",")
    const print_front = pf === "true"
    const print_rear = pr === "true"
    const sides = Max(ToNumber(print_front) + ToNumber(print_rear), 1)
    return {
      print_front,
      print_rear,
      renderer: renderer as ContentRenderer,
      security: securities.get(key)!,
      copies: ToNumber(copies),
      sheets: (count / sides) * ToNumber(copies),
    }
  })

  const per_sheet = total_depth / (Total(...grouped.map(g => g.sheets)) || 1)

  return grouped.map(g =>
    BuildBookBlock({
      ...g,
      sheets: g.sheets as PageNumber,
      // a cover is never among the pages: it is the book's cover
      is_cover: false,
      ...size,
      depth: (per_sheet * g.sheets) as Distance,
    })
  )
}

// A sheet as its grow draws it, its lengths read off `inner` and `dims`:
// its pieces when it has more than one, the outline its die cuts it to,
// curves, holes and all, and the cells it folds in from the piece `anchor`
// picks: none for a sheet with no crease and no die, drawn whole
const grow_sheet = (
  input: ImpositionSchema,
  inner: Rectangle<Distance>,
  kind: ComponentSchema["grow_kind"],
  dims: Pick<GrowSize, "depth" | "sheet_depth">,
  anchor: (leaf: GrowLeaf) => boolean
) => {
  const { opening_top } = input.orientation_kind
  const grow = ResolveGrow(
    kind.layout,
    GrowSideSize(inner, input.orientation_kind, dims),
    opening_top
  )
  const { outline, holes } = GrowOutline(kind.id, RecordMerge(inner, dims), opening_top)
  // the 3D folds each cell, whatever piece it is of
  const folds =
    grow.joints.length || outline.length
      ? GrowFolds(grow, anchor).map(({ leaf: _leaf, ...fold }) => fold)
      : []
  return {
    size: grow.size,
    pieces: grow.leaves.length > 1 ? GrowPieces(grow) : [],
    folds,
    ...(folds.length ? { outline, holes } : { outline: [], holes: [] }),
  }
}

export const BuildMeta3D = (
  input: ImpositionSchema,
  numbering: Numbering,
  has_book: boolean
): Meta3D["book"] => {
  const inner = ApplyOrientation(
    { width: input.size.width, height: input.size.height },
    input.orientation_kind
  )
  const spine = input.size_depth || CM(0.1)
  const opening_axis = input.orientation_kind.opening_top ? "y" : "x"
  // A single component, or the first of a job that binds no book, is drawn
  // as its sheet when it folds or its die cuts it: open, folding from the
  // panel page 1 is on, as thick as its stock. Anything else is a stack.
  const lone = !has_book || input.component_array.length === 1
  if (lone) {
    // The form holds one component at least
    const component = input.component_array[0]!
    const sheet_depth = component.sheet_depth ?? ZERO_DISTANCE
    const { size, ...sheet } = grow_sheet(
      input,
      inner,
      component.grow_kind,
      { depth: input.size_depth, sheet_depth },
      leaf => leaf.meta.page_front === 1
    )
    if (sheet.folds.length) {
      const jobs = input.component_array.flatMap(c => c.job_array)
      const fronts = numbering.printed.filter(p => p.is_front)
      return {
        blocks: [
          BuildBookBlock({
            print_front: jobs.some(j => j.print_mode.is_front),
            print_rear: jobs.some(j => j.print_mode.is_rear),
            sheets: Total(...fronts.map(p => copies_of(input, p))) as PageNumber,
            copies: component.copies,
            ...size,
            depth: sheet_depth,
            ...sheet,
            renderer: component.grow_kind.renderer,
            is_cover: component.grow_kind.is_cover,
            security: ComponentSecurity(component),
          }),
        ],
        blocks_offset: ZERO_POINT,
        opening_axis,
      }
    }
  }

  const cover_component = lone ? undefined : input.component_array.find(c => c.grow_kind.is_cover)
  let pages_iter = [...numbering.printed]
  let cover: Book3DCover | undefined
  let page_opening: Radian | undefined
  let cover_depth = ZERO_DISTANCE

  // A cover is drawn as one, folding round the block from its spine: a cover
  // of none, a back and a front with no crease, as the regular cover.
  if (cover_component) {
    const cover_kind = cover_component.grow_kind.is_grow
      ? cover_component.grow_kind
      : CoverGrowList.c
    // Any cover job may be the one printing (the first is often a "no" job).
    const cover_print_front = cover_component.job_array.some(j => j.print_mode.is_front)
    const cover_print_rear = cover_component.job_array.some(j => j.print_mode.is_rear)
    const cover_pages = ToNumber(cover_print_front) + ToNumber(cover_print_rear)
    const sheet_depth = cover_component.sheet_depth ?? ZERO_DISTANCE
    // A cover folds from its spine, a book's or a desk calendar's base: a
    // spine of none as 1 mm, so the cover doesn't degenerate
    const {
      size,
      folds: creased,
      ...sheet
    } = grow_sheet(
      input,
      inner,
      cover_kind,
      { depth: input.size_depth || MM(1), sheet_depth },
      leaf => leaf.kind === GrowContentKind.SPINE
    )
    // Boards that never open stand off the spine till they meet over it, a
    // desk calendar's base, the pages opening as far as they stand apart
    const stood = StandBoards(creased, input.orientation_kind.opening_top)
    const { folds } = stood
    page_opening = stood.opening
    if (folds.length) {
      cover_depth = sheet_depth
      pages_iter = pages_iter.slice(cover_pages)
      cover = BuildBookBlock({
        print_front: cover_print_front,
        print_rear: cover_print_rear,
        sheets: 1 as PageNumber,
        ...size,
        depth: sheet_depth,
        ...sheet,
        folds,
        renderer: cover_component.grow_kind.renderer,
        is_cover: true,
        security: ComponentSecurity(cover_component),
      })
    }
  }

  const blocks = build_blocks(
    input,
    pages_iter,
    inner,
    (spine - cover_depth) as Distance,
    cover ? cover_component : undefined
  )

  return {
    blocks,
    blocks_offset: ZERO_POINT,
    opening_axis,
    ...(cover ? { cover } : {}),
    ...(page_opening ? { page_opening } : {}),
  }
}
